/**
 * Colour maths for the design-token foundation (Phase 8).
 *
 * Pure, dependency-free helpers used to make the operator's configured brand
 * colour safe to apply at runtime:
 *   - convert a hex colour to an HSL triplet string ("45 97% 69%") that slots
 *     straight into a shadcn `hsl(var(--token))` CSS variable, and
 *   - pick a readable foreground (charcoal vs near-white) for any fill, using
 *     the WCAG 2.1 relative-luminance / contrast-ratio formulas.
 *
 * Everything here is side-effect free so it is trivially unit-testable; the
 * DOM-touching glue lives in `usePageBranding`.
 */

/** Design-system ink (charcoal — never pure black, per the design law). */
export const INK = '#303030';
/** Design-system inverse ink (warm near-white used for text on dark fills). */
export const INK_INVERSE = '#fdfbf5';

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Matches `#rgb` and `#rrggbb`, with or without the leading `#`. */
const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** True when `value` is a syntactically valid 3- or 6-digit hex colour. */
export function isValidHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

/** Parse a hex colour to 0–255 RGB channels. Returns `null` when invalid. */
export function hexToRgb(hex: string): Rgb | null {
  if (!isValidHex(hex)) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const int = Number.parseInt(h, 16);
  return { r: (int >> 16) & 0xff, g: (int >> 8) & 0xff, b: int & 0xff };
}

/**
 * WCAG 2.1 relative luminance of an sRGB colour (0 = black, 1 = white).
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * WCAG contrast ratio between two hex colours (1 → 21). Invalid inputs yield 1
 * (worst case) so callers safely treat them as "not readable".
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 1;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick the readable foreground (charcoal ink or inverse near-white) for text
 * placed on top of `background`, choosing whichever yields higher contrast.
 * Falls back to charcoal for invalid input.
 */
export function readableForeground(
  background: string,
  { dark = INK, light = INK_INVERSE }: { dark?: string; light?: string } = {},
): string {
  if (!isValidHex(background)) return dark;
  return contrastRatio(background, dark) >= contrastRatio(background, light) ? dark : light;
}

/**
 * Convert a hex colour to an `"H S% L%"` triplet (no `hsl(...)` wrapper), the
 * shape shadcn tokens expect. Returns `null` for invalid input.
 */
export function hexToHslTriplet(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Mix a hex colour toward white by `amount` (0 → unchanged, 1 → white).
 * Used to derive a soft brand tint (`--accent-soft`) from the primary colour.
 */
export function mixWithWhite(hex: string, amount: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const t = Math.min(1, Math.max(0, amount));
  const mix = (c: number): number => Math.round(c + (255 - c) * t);
  const toHex = (c: number): string => mix(c).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/** WCAG AA minimum contrast for normal-size text. */
export const AA_TEXT = 4.5;
/** WCAG AA minimum contrast for large text / UI graphics. */
export const AA_LARGE = 3;

/** Butter-yellow brand default — the design-system primary fill. */
export const BRAND_DEFAULT = '#fdd761';
/** Soft butter-yellow — the design-system `--accent-soft` default. */
export const BRAND_SOFT_DEFAULT = '#fff1be';

export interface ResolvedBranding {
  /** `"H S% L%"` triplet for `--primary`. */
  primaryHsl: string;
  /** `"H S% L%"` triplet for `--primary-foreground` (contrast-picked). */
  primaryForegroundHsl: string;
  /** Hex for the soft brand tint (`--accent-soft`). */
  accentSoftHex: string;
  /**
   * Hex for the readable interactive/link ink (`--accent-c`). Equals the brand
   * colour only when it clears AA against white; otherwise charcoal ink, so
   * links never become an unreadable pale colour.
   */
  accentInkHex: string;
  /** True when we fell back to the butter-yellow default (invalid/unusable input). */
  usedFallback: boolean;
}

/**
 * Resolve an operator-configured brand colour into the exact set of CSS custom
 * property values to apply, with two guards baked in:
 *   1. If the colour cannot carry either charcoal or near-white text at
 *      AA-large (an unusable mid-tone), fall back to the butter-yellow default.
 *   2. The interactive-ink (`--accent-c`) only adopts the brand colour when it
 *      is readable as text on white; otherwise it stays charcoal.
 *
 * Never returns values for semantic success/warning/info/danger tokens — those
 * are intentionally not brand-driven.
 */
export function resolveBranding(brandColor: string | null | undefined): ResolvedBranding {
  const fallback = (): ResolvedBranding => ({
    primaryHsl: hexToHslTriplet(BRAND_DEFAULT) as string,
    primaryForegroundHsl: hexToHslTriplet(readableForeground(BRAND_DEFAULT)) as string,
    accentSoftHex: BRAND_SOFT_DEFAULT,
    accentInkHex: INK,
    usedFallback: true,
  });

  if (!isValidHex(brandColor)) return fallback();
  const brand = brandColor.trim();

  const fg = readableForeground(brand);
  // Guard 1: the primary fill must carry its (normal-size) button label at AA.
  // A mid-tone that reaches neither charcoal nor near-white at 4.5:1 is unusable
  // as a primary colour — fall back to the butter-yellow default.
  if (contrastRatio(brand, fg) < AA_TEXT) return fallback();

  const primaryHsl = hexToHslTriplet(brand);
  const fgHsl = hexToHslTriplet(fg);
  if (!primaryHsl || !fgHsl) return fallback();

  // Guard 2: only let the brand colour drive link/emphasis text if it is
  // actually readable on a white surface; otherwise keep charcoal ink.
  const accentInkHex = contrastRatio(brand, '#ffffff') >= AA_TEXT ? brand : INK;

  return {
    primaryHsl,
    primaryForegroundHsl: fgHsl,
    accentSoftHex: mixWithWhite(brand, 0.85) ?? BRAND_SOFT_DEFAULT,
    accentInkHex,
    usedFallback: false,
  };
}
